import { PreKtasLabel } from "@/components/PreKtasLabel/PreKtasLabel";
import { tw } from "@/utils";
import {
  More,
  Consciousness,
  BloodPressure,
  HeartRate,
  Respiration,
  Temperature,
  OxygenSaturation,
  Electrocardiogram,
} from "./assets";
import { PatientStatus } from "./types";
import { ktasOpacityBgColors } from "@/components/styles";
import { convertChiefComplaints } from "@/data/symptom";

interface PatientStatusInfoProps {
  noOutline?: boolean;
  data: PatientStatus;
}

export const PatientStatusInfo = (props: PatientStatusInfoProps) => {
  const { noOutline, data } = props;

  const vitalSigns = buildVitalSignData(data);

  return (
    <div className="w-full h-fit bg-white rounded-[20px]">
      <div
        className={tw(
          "w-full h-fit p-[14px]",
          "border-solid border-[1px] border-gray-g75 rounded-[8px]",
          noOutline && "p-0 border-0"
        )}
      >
        <div
          className={tw(
            "flex items-center gap-[8px] w-full h-[44px] rounded-[6px] px-[12px] py-[10px]",
            ktasOpacityBgColors[data.preKtas]
          )}
        >
          <div className="text-base font-medium text-gray-g100">주호소</div>
          <div className="text-base font-semibold text-gray-g300">
            {/* todo: convertChiefComplaints 함수로 변경 필요 */}
            {convertChiefComplaints(data.chiefComplaint)}
          </div>
          <PreKtasLabel preKtas={data.preKtas} />
        </div>
        <div
          className={tw(
            "grid grid-cols-[1fr_1fr] grid-rows-3 items-center gap-x-[28px] gap-y-[12px]",
            "relative w-full h-fit mt-[18px] mb-[12px]"
          )}
        >
          {/* 세로선 */}
          <div className="absolute left-1/2 top-0 w-[1px] h-full bg-gray-g30" />
          {vitalSigns.map((data) => (
            <div key={data.category} className="flex items-center h-[34px]">
              <div className="flex items-center justify-center w-[30px] h-[30px] rounded-full bg-gray-g10">
                {vitalSignIcons[data.category]}
              </div>
              <div className="text-base font-medium text-gray-g200 ml-[12px]">
                {vitalSignLabels[data.category]}
              </div>
              <div className="flex items-center gap-[6px] ml-auto">
                {data.category === "electrocardiogram" ? (
                  <div
                    className="flex items-center gap-[6px]"
                    // onClick={() => {
                    //   alert("준비 중입니다.");
                    // }}
                  >
                    <div
                      className={tw(
                        "w-[8px] h-[8px] rounded-full",
                        data.value === "STEMI" ? "bg-red" : "bg-green"
                      )}
                    ></div>
                    <div
                      className={tw(
                        "text-base font-semibold text-green",
                        data.value === "STEMI" ? "text-red" : "text-green"
                      )}
                    >
                      {data.value === "STEMI" ? "STEMI" : "Stable"}
                    </div>
                    {/* <More className="ml-[4px] cursor-pointer" /> */}
                  </div>
                ) : (
                  <>
                    <div className={tw("text-base font-medium text-gray-g300")}>
                      {data.value}
                    </div>
                    {data.unit && (
                      <div className="text-sm font-regular text-gray-g100">
                        {data.unit}
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

type VitalSignCategory =
  | "consciousness"
  | "bloodPressure"
  | "heartRate"
  | "respiration"
  | "temperature"
  | "oxygenSaturation"
  | "electrocardiogram";

interface VitalData {
  category: VitalSignCategory;
  value: string;
  unit?: string;
  // 이상신호에 대한 판단 값 추가 필요
}

const vitalSignLabels: Record<VitalSignCategory, string> = {
  consciousness: "의식",
  bloodPressure: "혈압",
  heartRate: "심박수",
  respiration: "호흡수",
  temperature: "체온",
  oxygenSaturation: "SpO2",
  electrocardiogram: "ECG",
};

// 3. 색상 매핑 객체
const vitalSignColors: Record<VitalSignCategory, string> = {
  consciousness: "#17C8A8",
  bloodPressure: "#E66060",
  heartRate: "#F67E29",
  respiration: "#4A9BFC",
  temperature: "#D687E8",
  oxygenSaturation: "#7CBFE1",
  electrocardiogram: "#7D60E6",
};

const vitalSignIcons: Record<VitalSignCategory, JSX.Element> = {
  consciousness: (
    <Consciousness style={{ stroke: vitalSignColors.consciousness }} />
  ),
  bloodPressure: (
    <BloodPressure style={{ stroke: vitalSignColors.bloodPressure }} />
  ),
  heartRate: <HeartRate style={{ stroke: vitalSignColors.heartRate }} />,
  respiration: <Respiration style={{ stroke: vitalSignColors.respiration }} />,
  temperature: <Temperature style={{ stroke: vitalSignColors.temperature }} />,
  oxygenSaturation: (
    <OxygenSaturation style={{ stroke: vitalSignColors.oxygenSaturation }} />
  ),
  electrocardiogram: (
    <Electrocardiogram style={{ stroke: vitalSignColors.electrocardiogram }} />
  ),
};

export const defaultPatientStatus: PatientStatus = {
  isEmgcSym: false,
  preKtas: 5,
  chiefComplaint: [],
  vsSbp: 0,
  vsDbp: 0,
  vsBpm: 0,
  vsRr: 0,
  vsTemperature: 36.5,
  vsSpo2: 100,
  vsBloodSugar: 0,
  vsAvpu: "",
  ecg: "",
  createDtm: new Date().toISOString(), // 현재 시간 기준
};

const buildVitalSignData = (data: PatientStatus): VitalData[] => {
  const list: VitalData[] = [
    {
      category: "consciousness",
      value: data.vsAvpu || "Unknown",
    },
    {
      category: "bloodPressure",
      value: `${data.vsSbp}/${data.vsDbp}`,
      unit: "mmHg",
    },
    {
      category: "heartRate",
      value: `${data.vsBpm}`,
      unit: "bpm",
    },
    {
      category: "respiration",
      value: `${data.vsRr}`,
      unit: "회/분",
    },
    {
      category: "temperature",
      value: `${data.vsTemperature.toFixed(1)}`,
      unit: "℃",
    },
    {
      category: "oxygenSaturation",
      value: `${data.vsSpo2}`,
      unit: "%",
    },
    {
      category: "electrocardiogram",
      value: data.ecg || "Stable",
    },
  ];

  return list;
};
